import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function EvolutionScroll() {
    const { scrollYProgress } = useScroll();
    return (
        <section id="evolution" className="relative" data-testid="phase-evolution">
            <Scene
                index={1}
                title="The internet was built for humans."
                sub="Static pages. Click. Read. Repeat."
                anchor={0}
                Visual={SceneStatic}
            />
            <Scene
                index={2}
                title="But the next internet is autonomous."
                sub="Agents. Workflows. Self-routing signals."
                anchor={1}
                Visual={SceneAgents}
            />
            <Scene
                index={3}
                title="Autonomous systems need infrastructure."
                sub="Billions of agents. Fragmented models. Orchestration failures."
                anchor={2}
                Visual={SceneChaos}
            />
        </section>
    );
}

function Scene({ index, title, sub, Visual }) {
    return (
        <div className="relative h-screen w-full overflow-hidden border-t border-border" data-testid={`evolution-scene-${index}`}>
            <div className="absolute inset-0">
                <Visual />
            </div>
            <div className="absolute inset-0 vignette" />
            <div className="relative z-10 h-full flex items-end p-6 sm:p-12 lg:p-20">
                <div className="max-w-5xl">
                    <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-signal mb-4">
                        SCENE / 03
                    </div>
                    <motion.h2
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                        className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9]"
                    >
                        {title}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="mt-6 font-mono text-sm sm:text-base text-muted max-w-xl"
                    >
                        {sub}
                    </motion.p>
                </div>
            </div>
        </div>
    );
}

function SceneStatic() {
    return (
        <div className="absolute inset-0 bg-grid">
            <div className="absolute inset-0 grid grid-cols-6 grid-rows-4 gap-6 p-12 opacity-50">
                {Array.from({ length: 24 }).map((_, i) => (
                    <div key={i} className="border border-border bg-obsidian relative">
                        <div className="absolute top-2 left-2 right-2 h-1 bg-border" />
                        <div className="absolute top-5 left-2 right-8 h-0.5 bg-border" />
                        <div className="absolute top-7 left-2 right-12 h-0.5 bg-border" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function SceneAgents() {
    return (
        <div className="absolute inset-0">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
                <defs>
                    <radialGradient id="ag1" cx="50%" cy="50%">
                        <stop offset="0%" stopColor="#FFB000" stopOpacity="1" />
                        <stop offset="100%" stopColor="#FFB000" stopOpacity="0" />
                    </radialGradient>
                </defs>
                {Array.from({ length: 40 }).map((_, i) => {
                    const x = (i * 173) % 1200;
                    const y = (i * 91) % 800;
                    const tx = ((i + 7) * 173) % 1200;
                    const ty = ((i + 7) * 91) % 800;
                    return (
                        <g key={i}>
                            <line x1={x} y1={y} x2={tx} y2={ty} stroke="#FFB000" strokeOpacity="0.18" strokeWidth="0.6" />
                            <circle cx={x} cy={y} r="3" fill="url(#ag1)">
                                <animate attributeName="r" values="2;5;2" dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
                            </circle>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

function SceneChaos() {
    return (
        <div className="absolute inset-0">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
                {Array.from({ length: 220 }).map((_, i) => {
                    const x1 = (i * 53) % 1200;
                    const y1 = (i * 211) % 800;
                    const x2 = ((i + 11) * 167) % 1200;
                    const y2 = ((i + 11) * 89) % 800;
                    return (
                        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke={i % 7 === 0 ? "#FFB000" : "#FFFFFF"}
                            strokeOpacity={i % 7 === 0 ? "0.35" : "0.06"}
                            strokeWidth="0.5" />
                    );
                })}
            </svg>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-void" />
        </div>
    );
}
